import { useState } from 'react';
import { api } from '@/api/client';
import { CoverLetterResponse } from '@/api/types';
import { handleAppError } from '@/lib/errorUtils';
import { AIGenerationHookParams } from './ai/types';
import { APP_ERRORS } from '@/constants/errors';

export function useCoverLetter({
  workspaceId,
  selectedId,
  jobText,
  targetDetails,
  setStatus,
  setStatusMessage,
  recordUsage,
  setRightView,
  getSignal,
  setIsGenerating,
  scrollToResults,
}: AIGenerationHookParams) {
  const [coverLetterResult, setCoverLetterResult] = useState<CoverLetterResponse | null>(null);

  const handleGenerateCoverLetter = async (skipStateUpdates: boolean | any = false) => {
    const isSkip = skipStateUpdates === true;
    if (!selectedId || jobText.trim().length < 50) {
      if (!isSkip) {
        setStatus('error');
        setStatusMessage('Please enter a longer, meaningful job description (at least 50 characters) to generate a cover letter.');
      }
      return;
    }
    
    setIsGenerating(prev => ({ ...prev, coverLetter: true }));
    if (!isSkip) {
      setStatus('idle');
      setRightView('coverLetter');
      scrollToResults();
    }

    try {
      if (!isSkip) await new Promise(r => setTimeout(r, 300));
      const signal = isSkip ? getSignal('package') : getSignal('coverLetter');
      const result = await api.generateCoverLetter(workspaceId, { resumeId: selectedId, jobText, ...targetDetails }, { signal });
      if (result.usage) {
        recordUsage(result.usage.cost, result.usage.promptTokens, result.usage.completionTokens);
      }
      
      if (!result.markdown || result.markdown.length < 100) {
        throw new Error(APP_ERRORS.COVER_LETTER_FAILED.message);
      }

      setCoverLetterResult(result);
      if (!isSkip) setStatusMessage('Cover letter ready');
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      if (!isSkip) {
        setStatus('error');
        setStatusMessage(handleAppError(err, 'Generation failed'));
      }
    } finally {
      setIsGenerating(prev => ({ ...prev, coverLetter: false }));
    }
  };

  return { coverLetterResult, setCoverLetterResult, handleGenerateCoverLetter };
}
